import { Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { lazy, Suspense, useEffect, useRef } from "react";

import { Box, Flex, useColorModeValue } from "@chakra-ui/react";
import { ScrollContext } from "./context/ScrollContext";
import useResponsiveLayout from "./hooks/useResponsiveLayout";

import AddCoffeeShopPage from "./pages/AddCoffeeShopPage";
import ContactPage from "./pages/ContactPage";
import HomePage from "./pages/HomePage";
import ListsPage from "./pages/ListsPage";
import NotFoundPage from "./pages/NotFoundPage";
import PrivacyPage from "./pages/PrivacyPage";
import AboutPage from "./pages/AboutPage";
import ShopProfilePage from "./pages/ShopProfilePage";

import HomeMapBackdrop from "./components/common/HomeMapBackdrop";
import ShopProfileOverlay from "./components/common/ShopProfileOverlay";
import Header from "./components/layout/Header";
import AppBanner from "./components/layout/AppBanner";

const ExplorePage = lazy(() => import("./pages/ExplorePage"));
const ListDetailPage = lazy(() => import("./pages/ListDetailPage"));
const OwnerSubmissionPage = lazy(() => import("./pages/OwnerSubmissionPage"));
// Lazy so Firebase only downloads for /admin, never for regular visitors.
const AdminPage = lazy(() => import("./pages/AdminPage"));

const MotionBox = motion(Box);

function AppRoutes({ location }) {
  return (
    <Suspense fallback={<Box p={8} role="status">Loading page…</Box>}>
      <Routes location={location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/add-coffee-shop" element={<AddCoffeeShopPage />} />
        <Route path="/shop/:id" element={<ShopProfilePage />} />
        {/* Same uuid as /shop/:id, so you swap one word in the URL standing with the owner.
            ":id" of "new" is the blank cold-outreach form. */}
        <Route path="/owner/:id" element={<OwnerSubmissionPage />} />
        <Route path="/collections" element={<ListsPage />} />
        <Route path="/collections/:slug" element={<ListDetailPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route
          path="/not-listed"
          element={<NotFoundPage title="This shop is no longer listed" subtitle="It's no longer on Kuthé. Explore the map for independent coffee nearby." gaTitle="shop-not-listed" />}
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function AppInner() {
  const location = useLocation();
  const { isDesktopLayout } = useResponsiveLayout();
  const background = location.state?.backgroundLocation;
  // Desktop Explore → shop: keep Explore mounted; profile renders as overlay.
  const showShopOverlay =
    isDesktopLayout &&
    Boolean(background) &&
    location.pathname.startsWith("/shop/");
  const displayLocation = showShopOverlay ? background : location;

  const isHome = displayLocation.pathname === "/";
  const isCollections = displayLocation.pathname === "/collections";
  const isListDetail = /^\/collections\/[^/]+/.test(displayLocation.pathname);
  const isShopRoute = location.pathname.startsWith("/shop/");
  // Owner capture used to run chrome-free, on the reasoning that it was handed to an
  // owner mid-shift. In practice it's driven by us with the owner alongside, so the
  // page now sits in the site's own layout: same header, same nav, recognisably Kuthé
  // rather than a standalone form on an unfamiliar domain.
  // The app-download banner stays suppressed. It sells the consumer app to someone who
  // is filling in a form about their shop, which is the one thing on the page with
  // nothing to do with the task.
  const isOwnerRoute = displayLocation.pathname.startsWith("/owner/");
  // Shared SVG world map behind home + collections directory (not detail).
  const mapBleed = isHome || isCollections;
  // Home and collections pad their own top to clear the header; list detail brings its own
  // header bar. Everything else needs the shell to reserve room for the fixed header.
  const selfPaddedRoute = isHome || isCollections || isListDetail;
  const isExploreRoute = displayLocation.pathname === "/explore";
  // Explore + shop share tight mobile chrome so list→profile doesn't jump the header gap.
  const mobileTop = isExploreRoute || isShopRoute ? "64px" : "74px";
  const desktopTop = isExploreRoute || showShopOverlay ? "84px" : "108px";
  const paddingTop = isDesktopLayout
    ? desktopTop
    : `calc(${mobileTop} + env(safe-area-inset-top, 0px))`;
  const bg = useColorModeValue("#f5ede0", "#1a1209");
  const scrollRef = useRef(null);

  // Opacity route fades flash a blank cream frame when unmounting the map on mobile.
  const quietRoute =
    displayLocation.pathname === "/explore" ||
    displayLocation.pathname.startsWith("/shop/");

  useEffect(() => {
    // Don't yank Explore scroll when opening the profile panel.
    if (showShopOverlay) return;
    scrollRef.current?.scrollTo(0, 0);
  }, [location.pathname, showShopOverlay]);

  return (
    <Flex
      direction="column"
      w="100%"
      maxW="100vw"
      overflowX="hidden"
      sx={{ height: "100vh", "@supports (height: 100dvh)": { height: "100dvh" } }}
    >
      {!isListDetail && <Header />}
      {!isOwnerRoute && (isExploreRoute || isShopRoute || isListDetail) && <AppBanner />}
      <ScrollContext.Provider value={scrollRef}>
        <Flex
          ref={scrollRef}
          direction="column"
          overflowY="auto"
          overflowX="hidden"
          bg={bg}
          flex={1}
          position="relative"
          sx={{ scrollbarGutter: isDesktopLayout ? "stable both-edges" : "auto" }}
        >
          {/*
            Sticky map that cancels its own flow height (mb: -100vh).
            Content sits on top without a competing -100vh pull-up, so route
            swaps don't reflow/crop the SVG (which read as a zoom).
          */}
          {mapBleed && (
            <Box
              position="sticky"
              top={0}
              zIndex={0}
              overflow="hidden"
              pointerEvents="none"
              flexShrink={0}
              sx={{
                height: "100vh",
                marginBottom: "-100vh",
                "@supports (height: 100dvh)": {
                  height: "100dvh",
                  marginBottom: "-100dvh",
                },
              }}
            >
              <HomeMapBackdrop showPins={false} />
            </Box>
          )}

          <Box
            position="relative"
            zIndex={1}
            flex={1}
            display="flex"
            flexDirection="column"
            minH={mapBleed ? undefined : 0}
            pt={selfPaddedRoute ? 0 : paddingTop}
          >
            {mapBleed ? (
              <MotionBox
                key={displayLocation.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                flex={1}
                display="flex"
                flexDirection="column"
                w="100%"
              >
                <AppRoutes location={displayLocation} />
              </MotionBox>
            ) : (
              <AnimatePresence mode="wait" initial={false}>
                <MotionBox
                  key={displayLocation.pathname}
                  initial={quietRoute ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={quietRoute ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: quietRoute ? 0 : 0.12, ease: "easeInOut" }}
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  minH={0}
                  w="100%"
                >
                  <AppRoutes location={displayLocation} />
                </MotionBox>
              </AnimatePresence>
            )}
          </Box>
        </Flex>
      </ScrollContext.Provider>

      <AnimatePresence>
        {showShopOverlay && <ShopProfileOverlay key="shop-profile-overlay" />}
      </AnimatePresence>
    </Flex>
  );
}

function App() {
  return (
    <Router>
      <AppInner />
    </Router>
  );
}

export default App;
